/**
 * @fileOverview This file contains the implementation of the Item component for Inventory module
 * @version 1.0.0
 * @since 1.0.0
 * @author Miguel Ángel Bermea Rodríguez
 */

//#region Imports
import '../../scss/Components/Inventory/_item.scss';
import { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Tooltip,
  IconButton,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleEllipsis } from '@fortawesome/pro-duotone-svg-icons';
import ItemMenu from './ItemMenu';
import MovementModal from './MovementModal';
import InventoryUtils from '../../utils/inventoryUtils';
import EditItemModal from './EditItemModal';
import DeleteItemModal from './DeleteItemModal';
//#endregion

const Item = ({
  item_id,
  item_name,
  model,
  code,
  manufacturer_id,
  manufacturer_name,
  separated_inventory_id,
  inventory_type_id,
  inventory_type_name,
  location_id,
  default_ubication, // Location
  priority,
  min_stock,
  max_stock,
  cost,
  currency_id,
  currency_name,
  currency_symbol,
  unit_id,
  unit_name,
  image_url,
  image_name,
  initial_amount,
  total_inputs,
  total_outputs,
  last_update,
  final_amount, // Stock
  delta_amount, // Exits
  updated_at,
  refreshItems,
}) => {
  const [hoverOptions, setHoverOptions] = useState(false);
  const [anchorOptionsMenu, setAnchorOptionsMenu] = useState(null);

  const [showMovementModal, setShowMovementModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleMenuOpen = (event) => {
    setAnchorOptionsMenu(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorOptionsMenu(null);
    setHoverOptions(false);
  };

  const menuOpen = Boolean(anchorOptionsMenu);

  return (
    <Box
      position={'relative'}
      onMouseEnter={() => setHoverOptions(true)}
      onMouseLeave={() => setHoverOptions(false)}
    >
      {hoverOptions && (
        <Box>
          <IconButton
            sx={{
              position: 'absolute',
              top: -8,
              right: -8,
            }}
            size='medium'
            onClick={handleMenuOpen}
          >
            <FontAwesomeIcon
              icon={faCircleEllipsis}
              className='inventory-item-options-icon'
            />
          </IconButton>
          <ItemMenu
            anchorOptionsMenu={anchorOptionsMenu}
            menuOpen={menuOpen}
            handleMenuClose={handleMenuClose}
            setShowMovementModal={setShowMovementModal}
            setShowEditModal={setShowEditModal}
            setShowDeleteModal={setShowDeleteModal}
          />
        </Box>
      )}
      <Card
        sx={{
          minWidth: 200,
          maxWidth: 200,
          mx: 1,
          my: 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 100,
            backgroundColor: '#f0f0f0',
          }}
        >
          <img
            src={image_url}
            alt={item_name}
            style={{ maxWidth: '100%', maxHeight: '100%' }}
          />
        </Box>
        <CardContent sx={{ p: '1rem' }}>
          <Tooltip title={item_name}>
            <Box
              display='flex'
              gap='0.5rem'
              sx={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              <Typography className='inventory-item-id'>{code}</Typography>
              <Typography className='inventory-item-title'>
                {item_name}
              </Typography>
            </Box>
          </Tooltip>
          <Box className='inventory-item-content'>
            <Box display={'flex'}>
              <Typography
                variant='body2'
                className='inventory-item-content-label'
              >
                {model}
              </Typography>
            </Box>
            <Box display={'flex'} justifyContent={'space-between'}>
              <Typography
                variant='body2'
                className='inventory-item-content-label'
              >
                Almacén:
              </Typography>
              <Typography
                variant='body2'
                className='inventory-item-content-data'
              >
                {inventory_type_name}
              </Typography>
            </Box>
            <Box display={'flex'} justifyContent={'space-between'}>
              <Typography
                variant='body2'
                className='inventory-item-content-label'
              >
                Ubicación:
              </Typography>
              <Typography
                variant='body2'
                className='inventory-item-content-data'
              >
                {default_ubication}
              </Typography>
            </Box>
            <Box display={'flex'} justifyContent={'space-between'}>
              <Typography
                variant='body2'
                className='inventory-item-content-label'
              >
                Fabricante:
              </Typography>
              <Typography
                variant='body2'
                className='inventory-item-content-data'
              >
                {manufacturer_name}
              </Typography>
            </Box>
            <Box
              display={'flex'}
              flexDirection={'column'}
              gap={0.5}
              marginBottom={0.5}
              marginTop={0.5}
            >
              <Box
                width={'100%'}
                display={'flex'}
                justifyContent={'space-between'}
              >
                <Box
                  display={'flex'}
                  flexDirection={'column'}
                  alignItems={'center'}
                  backgroundColor={'#E8E8E8'}
                  borderRadius={1.5}
                  padding={0.5}
                  minWidth={'30%'}
                >
                  <Typography
                    variant='body2'
                    className='inventory-item-content-label'
                  >
                    En stock
                  </Typography>
                  <Typography
                    variant='body2'
                    className='inventory-item-content-data'
                    color={InventoryUtils.getStockColor(
                      min_stock,
                      max_stock,
                      final_amount
                    )}
                  >
                    {final_amount}
                  </Typography>
                </Box>
                <Box
                  display={'flex'}
                  flexDirection={'column'}
                  alignItems={'center'}
                  backgroundColor={'#E8E8E8'}
                  borderRadius={1.5}
                  padding={0.5}
                  minWidth={'30%'}
                >
                  <Typography
                    variant='body2'
                    className='inventory-item-content-label'
                  >
                    Mín.
                  </Typography>
                  <Typography
                    variant='body2'
                    className='inventory-item-content-data'
                  >
                    {min_stock}
                  </Typography>
                </Box>
                <Box
                  display={'flex'}
                  flexDirection={'column'}
                  alignItems={'center'}
                  backgroundColor={'#E8E8E8'}
                  borderRadius={1.5}
                  padding={0.5}
                  minWidth={'30%'}
                >
                  <Typography
                    variant='body2'
                    className='inventory-item-content-label'
                  >
                    Máx.
                  </Typography>
                  <Typography
                    variant='body2'
                    className='inventory-item-content-data'
                  >
                    {max_stock}
                  </Typography>
                </Box>
              </Box>
              <Box
                display={'flex'}
                gap={1}
                backgroundColor={'#E8E8E8'}
                borderRadius={1.5}
                padding={0.5}
              >
                <Box
                  width={'50%'}
                  display={'flex'}
                  justifyContent={'space-between'}
                >
                  <Typography
                    variant='body2'
                    className='inventory-item-content-label'
                  >
                    Salidas
                  </Typography>
                  <Typography
                    variant='body2'
                    className='inventory-item-content-data'
                  >
                    {total_outputs}
                  </Typography>
                </Box>
                <Box
                  width={'50%'}
                  display={'flex'}
                  justifyContent={'space-between'}
                >
                  <Typography
                    variant='body2'
                    className='inventory-item-content-label'
                  >
                    Entradas
                  </Typography>
                  <Typography
                    variant='body2'
                    className='inventory-item-content-data'
                  >
                    {total_inputs}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box display={'flex'} justifyContent={'space-between'}>
              <Typography
                variant='body2'
                className='inventory-item-content-label'
              >
                Fecha de act.
              </Typography>
              <Typography
                variant='body2'
                className='inventory-item-content-data'
              >
                {last_update ? last_update : '-'}
              </Typography>
            </Box>
          </Box>
        </CardContent>
        <Box
          sx={{
            width: '100%',
            height: '0.5rem',
            backgroundColor: `${
              priority === 0
                ? '#F8463B'
                : priority === 1
                ? '#FC842D'
                : priority === 2
                ? '#FFD850'
                : 'black'
            }`,
          }}
        ></Box>
      </Card>
      <MovementModal
        itemId={item_id}
        refreshItems={refreshItems}
        showMovementModal={showMovementModal}
        setShowMovementModal={setShowMovementModal}
        image={image_url}
        currency_symbol={currency_symbol}
        cost={cost}
        code={item_id}
        warehouseId={inventory_type_id}
        warehouse={inventory_type_name}
        locationId={location_id}
        location={default_ubication}
        stock={final_amount}
        minStock={min_stock}
        maxStock={max_stock}
        item_name={item_name}
        model={model}
        manufacturer_name={manufacturer_name}
      />
      <EditItemModal
        show={showEditModal}
        setShow={setShowEditModal}
        refreshItems={refreshItems}
        itemData={{
          item_id,
          item_name,
          model,
          code,
          manufacturer_id,
          manufacturer_name,
          separated_inventory_id,
          inventory_type_id,
          inventory_type_name,
          location_id,
          default_ubication,
          priority,
          min_stock,
          max_stock,
          cost,
          currency_id,
          currency_name,
          currency_symbol,
          unit_id,
          unit_name,
          image_name,
          image_url,
          initial_amount,
          final_amount,
          delta_amount,
        }}
      />
      <DeleteItemModal
        show={showDeleteModal}
        setShow={setShowDeleteModal}
        refreshItems={refreshItems}
        itemData={{
          item_id,
          item_name,
          model,
          code,
          manufacturer_name,
          default_ubication,
          inventory_type_name,
        }}
      />
    </Box>
  );
};

export default Item;
